<template>
  <div
    class="deal-card"
    :class="{
      'deal-card--selected': selected,
      'deal-card--won': deal.won_at,
      'deal-card--lost': deal.lost_at,
      'deal-card--compact': compact,
    }"
    @click="handleClick"
  >
    <!-- Compact Layout (List View) -->
    <template v-if="compact">
      <div class="deal-card__compact">
        <div class="deal-card__compact-main">
          <h4 class="deal-card__compact-name">{{ customerName || companyName || deal.title }}</h4>
          <span class="deal-card__compact-subtitle" v-if="companyName && customerName">
            {{ companyName }}
          </span>
        </div>
        <div class="deal-card__compact-right">
          <span class="deal-card__compact-value" v-if="deal.value">
            {{ formatCurrency(deal.value) }}
          </span>
          <span class="deal-card__compact-time" v-if="daysInStage !== null">
            {{ daysInStage }}d {{ $t('deals.in_stage') }}
          </span>
        </div>
      </div>
    </template>

    <!-- Full Card Layout (Kanban View) -->
    <template v-else>
      <div class="deal-card__heading">
        <p class="deal-card__name">{{ cardTitle }}</p>
        <p class="deal-card__amount">{{ formatCurrency(deal.value) }}</p>
      </div>
      <div class="deal-card__status">
        <DealOwnerPicker
          :owner="deal.owner"
          :owner-id="currentOwnerId"
          :members="members"
          @select="(ownerId) => $emit('assign-owner', ownerId)"
        />
        <span class="deal-card__signal" :class="`deal-card__signal--${signal.tone}`">
          <span class="deal-card__signal-main">
            <img :src="signal.icon" width="14" height="14" alt="" />
            <span class="deal-card__signal-text">{{ signal.label }} · {{ signal.age }}</span>
          </span>
          <span
            v-if="extraSignalCount"
            class="deal-card__signal-count"
            :title="$t('deals.status_more_signals', { count: extraSignalCount })"
          >
            +{{ extraSignalCount }}
          </span>
        </span>
      </div>
      <div class="deal-card__actions" @click.stop @mousedown.stop>
        <button
          type="button"
          class="deal-card__action"
          :disabled="!contactPhone"
          :title="$t('deals.call')"
          :aria-label="$t('deals.call')"
          @click="handlePhoneClick"
        >
          <img src="/icons/pipeline-action-phone.svg" alt="" />
        </button>
        <button
          type="button"
          class="deal-card__action"
          :disabled="!contactEmail"
          :title="$t('deals.email')"
          :aria-label="$t('deals.email')"
          @click="handleEmailClick"
        >
          <img src="/icons/pipeline-action-mail.svg" alt="" />
        </button>
        <button
          type="button"
          class="deal-card__action"
          :title="$t('deals.action_edit')"
          :aria-label="$t('deals.action_edit')"
          @click="handleClick"
        >
          <img src="/icons/pipeline-action-edit.svg" alt="" />
        </button>
        <span class="deal-card__action-divider"></span>
        <button
          type="button"
          class="deal-card__action deal-card__action--check"
          :title="$t('deals.action_select')"
          :aria-label="$t('deals.action_select')"
          :aria-pressed="selected"
          @click="handleSelect"
        >
          <span class="deal-card__check" :class="{ 'deal-card__check--on': selected }">
            <img v-if="selected" src="/icons/pipeline-action-check.svg" alt="" />
          </span>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed, getCurrentInstance } from 'vue';
  import DealOwnerPicker from '@/components/deals/DealOwnerPicker.vue';

  export default {
    name: 'DealCard',

    components: { DealOwnerPicker },

    props: {
      deal: {
        type: Object,
        required: true,
      },
      selected: {
        type: Boolean,
        default: false,
      },
      showCheckbox: {
        type: Boolean,
        default: false,
      },
      compact: {
        type: Boolean,
        default: false,
      },
      members: {
        type: Array,
        default: () => [],
      },
    },

    emits: ['click', 'select', 'email', 'assign-owner'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const formatCurrency = (value) => {
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0);
      };

      // Company name from lead or contact
      const companyName = computed(() => {
        return (
          props.deal.lead?.company_name ||
          props.deal.contact?.company_name ||
          props.deal.contact?.name ||
          props.deal.title
        );
      });

      // Customer name (klantnaam) from lead or contact
      const customerName = computed(() => {
        // Try lead name first
        if (props.deal.lead) {
          const lead = props.deal.lead;
          if (lead.first_name || lead.last_name) {
            return [lead.first_name, lead.last_name].filter(Boolean).join(' ');
          }
          if (lead.name) return lead.name;
        }
        // Try contact name
        if (props.deal.contact) {
          const contact = props.deal.contact;
          if (contact.first_name || contact.last_name) {
            return [contact.first_name, contact.last_name].filter(Boolean).join(' ');
          }
          if (contact.name) return contact.name;
        }
        return null;
      });

      // Tags (max 4 chips on the card, rest collapsed into "+N")
      const MAX_VISIBLE_TAGS = 4;
      const dealTags = computed(() => props.deal.tags || []);
      const visibleTags = computed(() => dealTags.value.slice(0, MAX_VISIBLE_TAGS));
      const hiddenTagsCount = computed(() =>
        Math.max(0, dealTags.value.length - MAX_VISIBLE_TAGS)
      );
      const tagStyle = (color) => {
        const c = color || '#64748b';
        return {
          backgroundColor: `${c}15`,
          color: c,
          borderColor: `${c}40`,
        };
      };

      // Days in current stage
      const daysInStage = computed(() => {
        if (props.deal.days_in_stage !== undefined) {
          return props.deal.days_in_stage;
        }
        if (props.deal.stage_entered_at) {
          const entered = new Date(props.deal.stage_entered_at);
          const now = new Date();
          const diffTime = Math.abs(now - entered);
          return Math.floor(diffTime / (1000 * 60 * 60 * 24));
        }
        return null;
      });

      // Latest activity (only 1)
      const latestActivity = computed(() => {
        const activities = props.deal.recent_activities || [];
        return activities.length > 0 ? activities[0] : null;
      });

      // Next open task (closest to now)
      // Supports: deal.next_task (single object from backend) OR deal.tasks/recent_tasks (array)
      const nextTask = computed(() => {
        // If backend provides next_task directly, use it
        if (props.deal.next_task) {
          return props.deal.next_task;
        }

        // Otherwise, find the next open task from tasks array
        const tasks = props.deal.tasks || props.deal.recent_tasks || [];
        // Filter open tasks and sort by due date
        const openTasks = tasks
          .filter((task) => task.status === 'pending' || task.status === 'in_progress')
          .sort((a, b) => {
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return new Date(a.due_date) - new Date(b.due_date);
          });
        return openTasks.length > 0 ? openTasks[0] : null;
      });

      // Task date helpers
      const isTaskOverdue = (task) => {
        if (!task.due_date) return false;
        const today = new Date().toISOString().split('T')[0];
        return task.due_date < today;
      };

      const isTaskToday = (task) => {
        if (!task.due_date) return false;
        const today = new Date().toISOString().split('T')[0];
        return task.due_date === today;
      };

      const formatTaskDue = (task) => {
        if (!task.due_date) return '';

        const d = new Date(task.due_date);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        // Check if overdue
        if (d < today && d.toDateString() !== today.toDateString()) {
          const diffDays = Math.floor((today - d) / (1000 * 60 * 60 * 24));
          return $t('common.days_ago', { count: diffDays });
        }

        // Check if today
        if (d.toDateString() === today.toDateString()) {
          return task.due_time ? task.due_time.slice(0, 5) : $t('common.today');
        }

        // Check if tomorrow
        if (d.toDateString() === tomorrow.toDateString()) {
          return $t('common.tomorrow');
        }

        return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
      };

      // Activity icon mapping
      const getActivityIcon = (type) => {
        const icons = {
          email: 'envelope',
          call: 'phone',
          whatsapp: 'whatsapp',
          meeting: 'calendar',
          note: 'file-text',
          task: 'check-circle-open',
        };
        return icons[type] || 'circle';
      };

      // Format activity time
      const formatActivityTime = (activity) => {
        const date = activity.due_date || activity.completed_at || activity.created_at;
        if (!date) return '';

        const now = new Date();
        const activityDate = new Date(date);
        const diffMs = activityDate - now;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        // Future dates
        if (diffDays === 0) {
          const hoursDiff = Math.floor(diffMs / (1000 * 60 * 60));
          if (hoursDiff >= 0 && hoursDiff < 24) return $t('common.today');
        }
        if (diffDays === 1) return $t('common.tomorrow');
        if (diffDays > 1 && diffDays <= 7) {
          return activityDate.toLocaleDateString('nl-NL', { weekday: 'long' });
        }

        // Past dates
        if (diffMs < 0) {
          const hoursDiff = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60));
          if (hoursDiff < 1) return $t('common.just_now');
          if (hoursDiff < 24) return $t('common.hours_ago', { count: hoursDiff });
          if (Math.abs(diffDays) <= 1) return $t('common.yesterday');
          return $t('common.days_ago', { count: Math.abs(diffDays) });
        }

        return activityDate.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
      };

      const cardTitle = computed(
        () => customerName.value || companyName.value || props.deal.title
      );

      // A deal is "inactive" once nothing has touched it for this many days
      const INACTIVE_DAYS = 7;
      const DAY_MS = 24 * 60 * 60 * 1000;

      const ACTIVITY_LABEL_KEYS = {
        call: 'deals.activity_types.call',
        email: 'deals.activity_types.email',
        email_sent: 'deals.activity_types.email',
        whatsapp: 'deals.activity_types.whatsapp',
        meeting: 'deals.activity_types.meeting',
      };

      const toDate = (value) => (value ? new Date(value) : null);

      const formatAge = (value) => {
        const date = value instanceof Date ? value : toDate(value);
        if (!date) return '';
        const minutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
        if (minutes < 60) return $t('deals.age_minutes', { count: minutes });
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return $t('deals.age_hours', { count: hours });
        return $t('deals.age_days', { count: Math.floor(hours / 24) });
      };

      const activityAt = (activity) =>
        toDate(activity?.completed_at || activity?.created_at);

      // Tasks without a time are only overdue once their due day has passed
      const overdueTaskAt = computed(() => {
        const task = nextTask.value;
        if (!task?.due_date) return null;
        const due = new Date(`${task.due_date}T${task.due_time || '23:59'}`);
        return due.getTime() < Date.now() ? due : null;
      });

      const proposalViewedAt = computed(() => toDate(props.deal.last_proposal_viewed_at));

      const stageEnteredAt = computed(() =>
        toDate(props.deal.stage_entered_at || props.deal.created_at)
      );

      const lastTouchAt = computed(() => {
        const dates = [activityAt(latestActivity.value), proposalViewedAt.value].filter(
          Boolean
        );
        if (dates.length === 0) return null;
        return dates.reduce((newest, date) => (date > newest ? date : newest));
      });

      const activityLabel = (activity) => {
        if (activity.type === 'note') return $t('deals.status_new_note');
        const key = ACTIVITY_LABEL_KEYS[activity.type];
        if (key) return $t(key);
        return activity.subject || $t('deals.status_new_note');
      };

      // The single most relevant signal for the card, mirroring the five
      // statuses in the pipeline design.
      const signal = computed(() => {
        if (overdueTaskAt.value) {
          return {
            tone: 'danger',
            icon: '/icons/pipeline-status-alert.svg',
            label: $t('deals.status_task_overdue'),
            age: formatAge(overdueTaskAt.value),
          };
        }

        const touch = lastTouchAt.value;

        if (touch && Date.now() - touch.getTime() < INACTIVE_DAYS * DAY_MS) {
          if (proposalViewedAt.value && proposalViewedAt.value.getTime() === touch.getTime()) {
            return {
              tone: 'neutral',
              icon: '/icons/pipeline-status-eye.svg',
              label: $t('deals.status_proposal_viewed'),
              age: formatAge(touch),
            };
          }

          return {
            tone: 'info',
            icon: '/icons/pipeline-status-note.svg',
            label: activityLabel(latestActivity.value),
            age: formatAge(touch),
          };
        }

        const since = touch || stageEnteredAt.value;

        if (since && Date.now() - since.getTime() >= INACTIVE_DAYS * DAY_MS) {
          return {
            tone: 'warning',
            icon: '/icons/pipeline-status-clock.svg',
            label: $t('deals.status_inactive'),
            age: formatAge(since),
          };
        }

        return {
          tone: 'neutral',
          icon: '/icons/pipeline-status-arrow.svg',
          label: $t('deals.status_new_in_stage'),
          age: formatAge(stageEnteredAt.value),
        };
      });

      // Only overdue deals surface a counter for the signals they hide
      const extraSignalCount = computed(() => {
        if (signal.value.tone !== 'danger') return 0;
        return (props.deal.recent_activities || []).length;
      });

      const currentOwnerId = computed(() => props.deal.owner_id || props.deal.owner?.id || null);

      // Contact info
      const contactPhone = computed(() => {
        return (
          props.deal.lead?.phone || props.deal.contact?.phone || props.deal.contact?.phone_number
        );
      });

      const contactEmail = computed(() => {
        return props.deal.lead?.email || props.deal.contact?.email;
      });

      const hasContactInfo = computed(() => {
        return contactPhone.value || contactEmail.value;
      });

      const handleClick = (event) => {
        emit('click', props.deal, event);
      };

      const handleSelect = (event) => {
        event.stopPropagation();
        emit('select', event);
      };

      const handlePhoneClick = () => {
        if (contactPhone.value) {
          window.location.href = `tel:${contactPhone.value}`;
        }
      };

      const handleEmailClick = () => {
        if (!contactEmail.value) return;
        emit('email');
      };

      return {
        $t,
        formatCurrency,
        companyName,
        customerName,
        visibleTags,
        hiddenTagsCount,
        tagStyle,
        daysInStage,
        latestActivity,
        nextTask,
        getActivityIcon,
        formatActivityTime,
        isTaskOverdue,
        isTaskToday,
        formatTaskDue,
        cardTitle,
        signal,
        extraSignalCount,
        currentOwnerId,
        contactPhone,
        contactEmail,
        hasContactInfo,
        handleClick,
        handleSelect,
        handlePhoneClick,
        handleEmailClick,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-card {
    position: relative;
    cursor: pointer;

    &:not(.deal-card--compact) {
      padding: 14px 16px 12px;
      background: #fff;
      border: 1px solid #e3e6ee;
      border-radius: 4px;
    }

    &__heading {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 16px;
    }

    &__name,
    &__amount {
      margin: 0;
      color: #000921;
      font-size: 13px;
      font-weight: 500;
      line-height: normal;
      white-space: nowrap;
    }

    &__name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__amount {
      flex-shrink: 0;
    }

    &__status {
      display: flex;
      align-items: center;
      gap: 4px;
      min-height: 34px;
      padding-top: 10px;
    }

    &__actions {
      display: none;
      position: absolute;
      top: 5px;
      right: 5px;
      z-index: 2;
      align-items: center;
      padding: 4px;
      background: #fff;
      border: 1px solid #d7d7d7;
      border-radius: 4px;
      box-shadow: 0 2px 4px rgba(0, 9, 33, 0.1);
    }

    &:hover:not(.deal-card--compact) &__actions,
    &--selected:not(.deal-card--compact) &__actions {
      display: flex;
    }

    &__action {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      border: 0;
      border-radius: 4px;
      background: transparent;
      cursor: pointer;

      img {
        display: block;
      }

      &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }

      @include hover {
        &:not(:disabled) {
          background: rgb(0 9 33 / 6%);
        }
      }

      &:focus-visible {
        outline: 2px solid #3364e6;
        outline-offset: 1px;
      }
    }

    &__action-divider {
      width: 1px;
      height: 16px;
      background: #e3e6ee;
    }

    &__check {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      background: #fff;
      border: 1px solid #d7d7d7;
      border-radius: 4px;

      &--on {
        background: #3364e6;
        border-color: #3364e6;
      }
    }

    &__signal {
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: space-between;
      gap: 4px;
      min-width: 0;
      color: #434f6f;

      &--info {
        color: #3364e6;
      }

      &--warning {
        color: #ff9500;
      }

      &--danger {
        color: #d90000;
      }
    }

    &__signal-main {
      display: flex;
      align-items: center;
      gap: 4px;
      min-width: 0;

      img {
        display: block;
        flex-shrink: 0;
      }
    }

    &__signal-text {
      overflow: hidden;
      font-size: 11px;
      font-weight: 300;
      line-height: 20px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__signal-count {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      height: 16px;
      padding: 0 5px;
      border-radius: 6px;
      background: #fce4e4;
      font-size: 10px;
      font-weight: 500;
      line-height: 20px;
      text-transform: uppercase;
    }

    &--selected:not(.deal-card--compact) {
      background: #eef3ff;
      border-color: #3364e6;
    }

    &--lost {
      opacity: 0.7;
    }

    &__checkbox {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      z-index: 1;

      input {
        width: 16px;
        height: 16px;
        cursor: pointer;
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;

      @include lg-down {
        gap: 0.5rem;
      }
    }

    // Header with company
    &__header {
      display: flex;
      align-items: flex-start;
    }

    &__company {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
    }

    &__company-icon {
      width: 36px;
      height: 36px;
      background: var(--slate-100);
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--slate-500);
      flex-shrink: 0;
    }

    &__company-info {
      flex: 1;
      min-width: 0;
    }

    &__title-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__company-name {
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-900);
      margin: 0;
      line-height: 1.3;

      @include lg-down {
        font-size: 0.8125rem;
      }
    }

    &__customer-name {
      font-size: 0.8125rem;
      font-weight: 400;
      color: var(--slate-600);
      margin-top: 0.25rem;
      line-height: 1.3;

      @include lg-down {
        display: none;
      }
    }

    &__value {
      font-size: 1rem;
      font-weight: 700;
      color: var(--slate-900);
      margin-top: 0.125rem;

      @include lg-down {
        font-size: 0.875rem;
      }
    }

    // Badges
    &__badges {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    // Tags
    &__tags {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      flex-wrap: wrap;
    }

    &__tag {
      display: inline-flex;
      align-items: center;
      gap: 0.3125rem;
      padding: 0.125rem 0.4375rem;
      border: 1px solid;
      border-radius: 0.375rem;
      font-size: 0.6875rem;
      font-weight: 500;
      line-height: 1.4;
      max-width: 140px;

      &--more {
        background: var(--slate-100);
        color: var(--slate-600);
        border-color: var(--slate-300);
      }
    }

    &__tag-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &__tag-text {
      @include ellipsis(1);
    }

    // Activity
    &__activity {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.8125rem;
      color: var(--slate-600);

      @include lg-down {
        display: none;
      }

      .icon {
        color: var(--slate-400);
        flex-shrink: 0;
      }
    }

    &__activity-subject {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__activity-time {
      font-size: 0.75rem;
      color: var(--slate-400);
      flex-shrink: 0;
    }

    // Task
    &__task {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.8125rem;
      color: var(--slate-600);
      padding: 0.5rem 0.625rem;
      background: var(--slate-50);
      border-radius: 0.375rem;
      border: 1px solid var(--slate-200);

      @include lg-down {
        display: none;
      }

      .icon {
        color: var(--brand-500);
        flex-shrink: 0;
      }
    }

    &__task-title {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 500;
    }

    &__task-due {
      font-size: 0.75rem;
      color: var(--slate-500);
      flex-shrink: 0;
      font-weight: 500;

      &--today {
        color: var(--blue-600);
      }

      &--overdue {
        color: var(--red-600);
      }
    }

    // Footer
    &__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-100);

      @include lg-down {
        display: none;
      }
    }

    &__owner-placeholder {
      width: 24px;
      height: 24px;
      background: var(--slate-200);
      border-radius: 50%;
    }

    &__owner-name {
      font-size: 0.8125rem;
      color: var(--slate-600);
      font-weight: 500;
    }

    &__quick-actions {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      opacity: 0;
      transition: opacity 0.15s ease;
    }

    &__quick-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 0.375rem;
      color: var(--slate-500);
      text-decoration: none;
      transition: background 0.15s ease, color 0.15s ease;

      &:hover {
        background: var(--brand-50);
        color: var(--brand-600);
      }
    }

    // Compact variant (list view)
    &--compact {
      background: #fff;
      border-bottom: 1px solid var(--slate-100);
      padding: 0.75rem 1rem;

      &:hover {
        background: var(--slate-50);
      }

      &:last-child {
        border-bottom: none;
      }
    }

    &__compact {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }

    &__compact-main {
      flex: 1;
      min-width: 0;
    }

    &__compact-name {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
      margin: 0;
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__compact-subtitle {
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.3;
    }

    &__compact-right {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      flex-shrink: 0;
    }

    &__compact-value {
      font-size: 0.875rem;
      font-weight: 700;
      color: var(--slate-900);
    }

    &__compact-time {
      font-size: 0.6875rem;
      color: var(--slate-400);
    }
  }
</style>
